import { cx } from '@codegouvfr/react-dsfr/fr/cx';
import Tag from '@codegouvfr/react-dsfr/Tag';
import clsx from 'clsx';
import { format } from 'date-fns';
import { omit } from 'lodash-es';
import { DepartmentLabels } from 'maestro-shared/referential/Department';
import { Regions } from 'maestro-shared/referential/Region';
import { StageLabels } from 'maestro-shared/referential/Stage';
import type { Pagination } from 'maestro-shared/schema/commons/Pagination';
import type { Laboratory } from 'maestro-shared/schema/Laboratory/Laboratory';
import {
  type MatrixReferential,
  matricesOfKind,
  matrixKindLabel,
  matrixLabel
} from 'maestro-shared/schema/Matrix/MatrixReferential';
import { ContextLabels } from 'maestro-shared/schema/ProgrammingPlan/Context';
import type { ProgrammingPlanDomain } from 'maestro-shared/schema/ProgrammingPlan/ProgrammingPlanDomain';
import type { ProgrammingPlanChecked } from 'maestro-shared/schema/ProgrammingPlan/ProgrammingPlans';
import { subPlanLabel } from 'maestro-shared/schema/ProgrammingPlan/ProgrammingSubPlan';
import type { FindSampleOptions } from 'maestro-shared/schema/Sample/FindSampleOptions';
import { SampleComplianceLabels } from 'maestro-shared/schema/Sample/SampleCompliance';
import { SampleStatusLabels } from 'maestro-shared/schema/Sample/SampleStatus';
import type { UserListItem } from 'maestro-shared/schema/User/User';
import { isDefinedAndNotNull } from 'maestro-shared/utils/utils';
import { Fragment, type ReactNode, useMemo } from 'react';
import { useAuthentication } from 'src/hooks/useAuthentication';
import { useMatrixReferential } from 'src/hooks/useMatrixReferential';
import type { PrescriptionFilters } from '../../store/reducers/prescriptionsSlice';

type FilterableType = FindSampleOptions &
  Omit<
    PrescriptionFilters,
    | 'year'
    | 'missingDistribution'
    | 'missingLaboratory'
    | 'withNovelty'
    | 'contexts'
  >;

interface Props {
  title?: string;
  filters: Partial<FilterableType>;
  onChange: (filters: Partial<FilterableType>) => void;
  users?: UserListItem[];
  programmingPlans?: ProgrammingPlanChecked[];
  laboratories?: Pick<Laboratory, 'id' | 'name'>[];
  domains?: ProgrammingPlanDomain[];
}

const tagProps = {
  dismissible: true,
  small: true,
  className: clsx(cx('fr-mb-1v'), 'align-left')
};

type FilterableProp = keyof Omit<
  FilterableType,
  keyof Pagination | 'reference' | 'companySirets' | 'prescriptionId'
>;

const renderArrayTags = <T extends string>(
  filterKey: string,
  value: T[],
  getLabel: (item: T) => string | null | undefined,
  onChange: (filters: Partial<FilterableType>) => void,
  extraChanges?: Partial<FilterableType>
): ReactNode => (
  <Fragment key={`tag-${filterKey}`}>
    {value.map((item) => {
      const label = getLabel(item);
      return label ? (
        <Tag
          {...tagProps}
          key={`tag-${filterKey}-${item}`}
          nativeButtonProps={{
            onClick: () =>
              onChange({
                [filterKey]: value.filter((v) => v !== item),
                ...extraChanges
              } as Partial<FilterableType>)
          }}
        >
          {label}
        </Tag>
      ) : null;
    })}
  </Fragment>
);

const filtersConfig = {
  matrixKinds: {
    prop: 'matrixKinds',
    getComponent: (value, onChange, { filters, matrixReferential }) => (
      <Fragment key="tag-matrixKinds">
        {value.map((kind) => (
          <Tag
            {...tagProps}
            key={`tag-matrixKind-${kind}`}
            nativeButtonProps={{
              onClick: () => {
                const newMatrixKinds = value.filter((v) => v !== kind);
                const remainingMatrices = filters?.matrices?.filter((m) =>
                  newMatrixKinds.some((k) =>
                    matricesOfKind(matrixReferential, k).includes(m)
                  )
                );
                onChange({
                  matrixKinds: newMatrixKinds,
                  matrices: remainingMatrices
                });
              }
            }}
          >
            {matrixKindLabel(matrixReferential, kind)}
          </Tag>
        ))}
      </Fragment>
    )
  },
  matrices: {
    prop: 'matrices',
    getComponent: (value, onChange, { matrixReferential }) =>
      renderArrayTags(
        'matrices',
        value,
        (m) => matrixLabel(matrixReferential, m),
        onChange
      )
  },
  statuses: {
    prop: 'statuses',
    getComponent: (value, onChange) =>
      renderArrayTags('statuses', value, (s) => SampleStatusLabels[s], onChange)
  },
  sampledBy: {
    prop: 'sampledBy',
    getComponent: (value, onChange, { users }) =>
      renderArrayTags(
        'sampledBy',
        value,
        (id) => users?.find((user) => user.id === id)?.name,
        onChange
      )
  },
  sampledDate: {
    prop: 'sampledDate',
    getLabel: (value) =>
      `Date de prélèvement : ${format(new Date(value), 'dd/MM/yyyy')}`
  },
  sentDate: {
    prop: 'sentDate',
    getLabel: (value) =>
      `Date d'envoi de la DAI : ${format(new Date(value), 'dd/MM/yyyy')}`
  },
  regions: {
    prop: 'regions',
    getComponent: (value, onChange) =>
      renderArrayTags('regions', value, (r) => Regions[r].name, onChange, {
        departments: undefined
      })
  },
  departments: {
    prop: 'departments',
    getComponent: (value, onChange) =>
      renderArrayTags(
        'departments',
        value,
        (d) => DepartmentLabels[d],
        onChange
      )
  },
  contexts: {
    prop: 'contexts',
    getComponent: (value, onChange) =>
      renderArrayTags('contexts', value, (d) => ContextLabels[d], onChange)
  },
  stage: {
    prop: 'stage',
    getLabel: (value) => StageLabels[value]
  },
  programmingPlanDomainIds: {
    prop: 'programmingPlanDomainIds',
    getComponent: (value, onChange, { domains }) =>
      renderArrayTags(
        'programmingPlanDomainIds',
        value,
        (id) => domains?.find((domain) => domain.id === id)?.label,
        onChange
      )
  },
  coordinatorIds: {
    prop: 'coordinatorIds',
    getComponent: (value, onChange, { users }) =>
      renderArrayTags(
        'coordinatorIds',
        value,
        (id) => users?.find((user) => user.id === id)?.name,
        onChange
      )
  },
  compliance: {
    prop: 'compliance',
    getLabel: (value) => SampleComplianceLabels[value]
  },
  withAtLeastOneResidue: {
    prop: 'withAtLeastOneResidue',
    getLabel: () => 'Avec au moins un résidu'
  },
  withAtLeastOneCopiedItem: {
    prop: 'withAtLeastOneCopiedItem',
    getLabel: () => 'Avec plusieurs exemplaires'
  },
  laboratoryIds: {
    prop: 'laboratoryIds',
    getComponent: (value, onChange, { laboratories }) =>
      renderArrayTags(
        'laboratoryIds',
        value,
        (id) => laboratories?.find((laboratory) => laboratory.id === id)?.name,
        onChange
      )
  },
  programmingPlanIds: {
    prop: 'programmingPlanIds',
    getComponent: (value, onChange, { programmingPlans }) =>
      renderArrayTags(
        'programmingPlanIds',
        value,
        (id) => programmingPlans?.find((plan) => plan.id === id)?.title ?? id,
        onChange
      )
  },
  programmingSubPlanIds: {
    prop: 'programmingSubPlanIds',
    getComponent: (value, onChange, { programmingPlans, matrixReferential }) =>
      renderArrayTags(
        'programmingSubPlanIds',
        value,
        (id) => {
          const subPlan = programmingPlans
            ?.flatMap((plan) => plan.subPlans)
            .find((subPlan) => subPlan.id === id);
          return subPlan ? subPlanLabel(matrixReferential, subPlan) : id;
        },
        onChange
      )
  }
} as const satisfies {
  [key in FilterableProp]: {
    prop: key;
  } & (
    | {
        getLabel: (
          value: NonNullable<FilterableType[key]>,
          data: Record<string, never>
        ) => string | null;
        getComponent?: never;
      }
    | {
        getComponent: (
          value: NonNullable<FilterableType[key]>,
          onChange: (filters: Partial<FilterableType>) => void,
          data: {
            programmingPlans?: ProgrammingPlanChecked[];
            users?: UserListItem[];
            laboratories?: Props['laboratories'];
            domains?: ProgrammingPlanDomain[];
            filters?: Partial<FilterableType>;
            matrixReferential: MatrixReferential;
          }
        ) => ReactNode;
        getLabel?: never;
      }
  );
};

const FiltersTags = ({
  title,
  filters,
  onChange,
  users,
  programmingPlans,
  laboratories,
  domains
}: Props) => {
  const { hasNationalView } = useAuthentication();
  const matrixReferential = useMatrixReferential();

  const hasFilters = useMemo(
    () =>
      Object.values(
        omit(filters, 'regions', 'page', 'perPage', 'programmingPlanIds')
      ).some((value) => isDefinedAndNotNull(value) && value !== '') ||
      ((programmingPlans ?? []).length > 1 &&
        (filters.programmingPlanIds ?? []).length > 0) ||
      ((filters.regions ?? []).length > 0 && hasNationalView),
    [filters, hasNationalView, programmingPlans]
  );

  if (!hasFilters) {
    return null;
  }

  return (
    <div
      className={clsx('d-flex-align-start')}
      style={{ flexDirection: 'column' }}
    >
      {title && (
        <span
          className={cx('fr-text--light', 'fr-text--sm', 'fr-mb-0', 'fr-mt-3w')}
        >
          {title}
        </span>
      )}
      <div className={cx('fr-mt-1v')}>
        {Object.values(filtersConfig).map((conf) => {
          const value = filters[conf.prop];

          if (
            isDefinedAndNotNull(value) &&
            (!Array.isArray(value) || value.length > 0) &&
            (hasNationalView || conf.prop !== 'regions')
          ) {
            if ('getComponent' in conf) {
              // @ts-expect-error TS2345 il est perdu
              return conf.getComponent(value, onChange, {
                programmingPlans,
                users,
                laboratories,
                domains,
                filters,
                matrixReferential
              });
            } else {
              // @ts-expect-error TS2345 il est perdu
              const label = conf.getLabel(value, {});
              if (label) {
                return (
                  <Tag
                    {...tagProps}
                    key={conf.prop}
                    nativeButtonProps={{
                      onClick: () => onChange({ [conf.prop]: undefined })
                    }}
                  >
                    {label}
                  </Tag>
                );
              }
            }
          }
          return null;
        })}
      </div>
    </div>
  );
};

export default FiltersTags;
